import { useQuery } from "@tanstack/react-query";
import { agentAccessApi } from "@/api/agent-access";
import { useTenant } from "@/context/tenant-context";

/**
 * Per-agent permissions for the current user.
 *
 * Tenant admins implicitly have full access to every agent and don't
 * need a row in `agent_access`. Agent admins manage only the agents
 * they have an explicit grant for (directly or via group). Standard
 * users can chat with granted agents but cannot manage them.
 *
 * `hasAccess`  — the user can see/chat with this agent.
 * `canManage`  — the user can edit settings (config, secrets, MCP/
 *                skills, access tab, subagents). Only true for tenant
 *                admins and for agent admins with an explicit grant.
 *
 * The result derives from the server's `/companies/:id/accessible-agents`
 * endpoint, which is already authoritative: tenant admins get every
 * agent, everyone else gets only their grants.
 */
export function useAgentAccess(agentId: string | null | undefined) {
  const { tenantId, isTenantAdmin, isAgentAdmin } = useTenant();

  const { data: accessible, isLoading } = useQuery<{ id: string }[]>({
    queryKey: ["accessible-agents", tenantId],
    queryFn: () => agentAccessApi.listAccessible(tenantId) as Promise<{ id: string }[]>,
    enabled: !!tenantId && !isTenantAdmin,
  });

  if (isTenantAdmin) {
    return { hasAccess: true, canManage: true, isLoading: false };
  }
  if (!agentId) {
    return { hasAccess: false, canManage: false, isLoading: false };
  }
  if (isLoading) {
    return { hasAccess: false, canManage: false, isLoading: true };
  }
  const hasAccess = (accessible ?? []).some((a) => a.id === agentId);
  const canManage = hasAccess && isAgentAdmin;
  return { hasAccess, canManage, isLoading: false };
}
